import { Injectable } from '@angular/core';
import type { FormArray, FormGroup, ValidatorFn } from '@angular/forms';
import { FormControl, NonNullableFormBuilder } from '@angular/forms';
import type {
  BackendDeleteResponse,
  BackendPushErrorResponse,
  BackendPushResponse,
  BackendUpdateResponse,
} from '@freelancer/datastore';
import { Datastore, OrderByDirection } from '@freelancer/datastore';
import type {
  Currency,
  ExchangeRatesCollection,
  ProjectBudgetOption,
  ProjectBudgetOptionsCollection,
  QuotationCreateParams,
  QuotationItemRevision,
  QuotationItemRevisionsCollection,
  QuotationLogosCollection,
  QuotationRevision,
  QuotationRevisionsCollection,
} from '@freelancer/datastore/collections';
import { QuotationItemType } from '@freelancer/datastore/collections';
import { FreelancerCurrencyPipe } from '@freelancer/pipes';
import { TimeUtils } from '@freelancer/time-utils';
import {
  currentOrFutureDate,
  dateFormat,
  maxDecimalPlaces,
  maxLength,
  maxValue,
  minValue,
  required,
} from '@freelancer/ui/validators';
import { isDefined } from '@freelancer/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ProjectTypeApi } from 'api-typings/projects/projects';
import type { QuotationItemUnitTypeApi } from 'api-typings/quotations/quotations';
import {
  QuotationContextTypeApi,
  QuotationItemBillingFrequencyApi,
  QuotationItemStatusApi,
  QuotationStatusApi,
  QUOTATION_DESCRIPTION_MAX_LENGTHApi,
  QUOTATION_DESCRIPTION_MIN_LENGTHApi,
  QUOTATION_TITLE_MAX_LENGTHApi,
} from 'api-typings/quotations/quotations';
import type { Observable } from 'rxjs';
import { firstValueFrom, of, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { LexicalOrdering } from '../lexical-ordering/lexical-ordering.service';
import { QuotationFormHelperModule } from './quotation-form-helper.module';
import type {
  QuotationBidDetailFormGroup,
  QuotationDetailFormGroup,
  QuotationItemFormGroup,
  ServiceOfferingPrice,
} from './quotation-form-helper.types';
import type {
  ItemDetails,
  ItemRevisionParams,
  ItemUpdateParams,
  QuotationRevisionUpdateParams,
} from './quotation-form.model';
import {
  FormKeys,
  ItemFormKeys,
  MAX_ITEM_NAME_LENGTH,
} from './quotation-form.model';
import {
  validateFixedItemsTotal,
  validateFixedProjectBidDateRange,
  validateHourlyRate,
  validateMinimum,
  validateMinLengthTrimmed,
  validateSubscriptionAmount,
  validateSubscriptionTotal,
  validateWeeklyLimit,
} from './quotation-form.validators';

// New quotations will already have an expiry set in the backend
// This is in case any old quotation drafts are revisited without an expiry
export const DEFAULT_EXPIRY_DAYS = 30;
export const DEFAULT_TIMELINE_PERIOD = 7;

const MAX_HOURLY_RATE = 20_000;
const MAX_WEEKLY_LIMIT = 24 * 7;
const MIN_WEEKLY_LIMIT = 1;
const AUSTRALIAN_MINIMUM_HOURLY_BID_AMOUNT = 25;

/**
 * Helper Service for Quotation Form Building.
 *
 * Includes methods to create and initialise forms that should be injected in the Quotation
 * details and item form components.
 *
 * Also includes methods for updating quotation item revision and quotation revision
 * datastore collections.
 */
@UntilDestroy({ className: 'QuotationFormHelper' })
@Injectable({ providedIn: QuotationFormHelperModule })
export class QuotationFormHelper {
  /** Counter for how many api calls are currently in transit */
  apiCallsInTransit = 0;
  /** A Promise that resolves once the quotation is able to be submitted */
  quoteSubmittablePromise?: Promise<undefined>;
  /** Resolution function to fulfil `quoteSubmittablePromise` */
  quoteSubmitPromiseResolver?: (
    value: PromiseLike<undefined> | undefined,
  ) => void;
  private latestBackendErrorResponseSubject$ = new Subject<
    BackendPushErrorResponse<QuotationRevisionsCollection>
  >();
  private latestBackendErrorResponse$ =
    this.latestBackendErrorResponseSubject$.asObservable();

  constructor(
    private datastore: Datastore,
    private fb: NonNullableFormBuilder,
    private currencyPipe: FreelancerCurrencyPipe,
    private order: LexicalOrdering,
    private timeUtils: TimeUtils,
  ) {}

  // Create and initialise Quotation Details Form to initialise QuotationDetailsFormComponent.
  initialiseQuotationDetailsForm({
    allowZeroDayTimeline = false,
    initTimeline = true,
    initExpiryDate = true,
    initDuration = false,
    isEditingApprovedQuote, // If true, will omit several form controls like start date, which is no longer required
  }:
    | {
        allowZeroDayTimeline?: boolean;
        initTimeline?: boolean;
        initExpiryDate?: true;
        initDuration?: false;
        isEditingApprovedQuote?: boolean;
      }
    | {
        allowZeroDayTimeline?: boolean;
        initTimeline?: boolean;
        initExpiryDate?: false;
        initDuration?: true;
        isEditingApprovedQuote?: boolean;
      } = {}): FormGroup<QuotationDetailFormGroup> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const oneWeekFromToday = new Date();
    oneWeekFromToday.setDate(today.getDate() + DEFAULT_TIMELINE_PERIOD);
    oneWeekFromToday.setHours(0, 0, 0, 0);

    const defaultExpiryDate = new Date();
    defaultExpiryDate.setDate(today.getDate() + DEFAULT_EXPIRY_DAYS);
    const defaultExpiryTimestamp = getEndOfDayTimestamp(defaultExpiryDate);

    const detailsForm =
      isEditingApprovedQuote === undefined
        ? this.fb.group<QuotationDetailFormGroup>({
            [FormKeys.TITLE]: this.fb.control('', [
              required($localize`Enter a title for your quote`),
              maxLength(
                QUOTATION_TITLE_MAX_LENGTHApi,
                $localize`Quote title should be at most ${QUOTATION_TITLE_MAX_LENGTHApi} characters`,
              ),
            ]),
            [FormKeys.DESCRIPTION]: this.fb.control('', [
              required($localize`Enter a quote description`),
              validateMinLengthTrimmed(
                QUOTATION_DESCRIPTION_MIN_LENGTHApi,
                $localize`Quote description should be at least ${QUOTATION_DESCRIPTION_MIN_LENGTHApi} characters`,
              ),
              maxLength(
                QUOTATION_DESCRIPTION_MAX_LENGTHApi,
                $localize`Quote description should be at most ${QUOTATION_DESCRIPTION_MAX_LENGTHApi} characters`,
              ),
            ]),
          })
        : this.fb.group<QuotationDetailFormGroup>({
            [FormKeys.DESCRIPTION]: this.fb.control('', [
              required($localize`Enter a quote description`),
              validateMinLengthTrimmed(
                QUOTATION_DESCRIPTION_MIN_LENGTHApi,
                $localize`Quote description should be at least ${QUOTATION_DESCRIPTION_MIN_LENGTHApi} characters`,
              ),
              maxLength(
                QUOTATION_DESCRIPTION_MAX_LENGTHApi,
                $localize`Quote description should be at most ${QUOTATION_DESCRIPTION_MAX_LENGTHApi} characters`,
              ),
            ]),
          });

    if (initTimeline) {
      detailsForm.addControl(
        FormKeys.TIMELINE,
        this.fb.group(
          {
            ...(!isEditingApprovedQuote && {
              [FormKeys.START_DATE]: this.fb.control(today, [
                dateFormat($localize`Enter a valid date.`),
                currentOrFutureDate($localize`You cannot enter a past date.`),
                required($localize`Enter a start date.`),
              ]),
            }),
            [FormKeys.END_DATE]: this.fb.control(oneWeekFromToday, [
              dateFormat($localize`Enter a valid date.`),
              currentOrFutureDate($localize`You cannot enter a past date.`),
              required($localize`Enter an end date.`),
            ]),
          },
          {
            // If we disallow Quote periods with 0 days, we need to prevent past dates and
            // present date in the endDate input.
            validators: validateFixedProjectBidDateRange({
              allowZeroDayTimeline,
              startDateKey: 'startDate',
              endDateKey: 'endDate',
              errorText: $localize`End date must be after start date.`,
            }),
          },
        ),
      );
    }

    if (initExpiryDate) {
      detailsForm.addControl(
        FormKeys.EXPIRY_DATE,
        this.fb.control(new Date(defaultExpiryTimestamp), [
          dateFormat($localize`Enter a valid date.`),
          currentOrFutureDate($localize`You cannot enter a past date.`),
          required($localize`Enter an expiry date.`),
        ]),
      );
    }

    if (initDuration) {
      detailsForm.addControl(
        FormKeys.DURATION,
        this.fb.group({
          [FormKeys.DURATION_AMOUNT]: this.fb.control(1, [
            required($localize`Enter a duration.`),
            minValue(1, $localize`Duration cannot be 0.`),
          ]),
          [FormKeys.DURATION_UNIT]: this.fb.control('', [
            required(
              $localize`You must select one of these options to proceed`,
            ),
          ]),
        }),
      );
    }

    return detailsForm;
  }

  // Create and initialise Quotation details section of the Quotation Bid Form
  initialiseBidDetailsForm(
    allowZeroDayTimeline = false,
  ): FormGroup<QuotationBidDetailFormGroup> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const oneWeekFromToday = new Date();
    oneWeekFromToday.setDate(today.getDate() + DEFAULT_TIMELINE_PERIOD);
    oneWeekFromToday.setHours(0, 0, 0, 0);

    const detailsForm = this.fb.group<QuotationBidDetailFormGroup>({
      [FormKeys.DESCRIPTION]: this.fb.control('', [
        required($localize`Enter a quote description`),
        validateMinLengthTrimmed(
          QUOTATION_DESCRIPTION_MIN_LENGTHApi,
          $localize`Quote description should be at least ${QUOTATION_DESCRIPTION_MIN_LENGTHApi} characters`,
        ),
        maxLength(
          QUOTATION_DESCRIPTION_MAX_LENGTHApi,
          $localize`Quote description should be at most ${QUOTATION_DESCRIPTION_MAX_LENGTHApi} characters`,
        ),
      ]),
      [FormKeys.TIMELINE]: this.fb.group(
        {
          [FormKeys.START_DATE]: this.fb.control(today, [
            dateFormat($localize`Enter a valid date.`),
            currentOrFutureDate($localize`You cannot enter a past date.`),
            required($localize`Enter a start date.`),
          ]),
          [FormKeys.END_DATE]: this.fb.control(oneWeekFromToday, [
            dateFormat($localize`Enter a valid date.`),
            currentOrFutureDate($localize`You cannot enter a past date.`),
            required($localize`Enter an end date.`),
          ]),
        },
        {
          // If we disallow Quote periods with 0 days, we need to prevent past dates and
          // present date in the endDate input.
          validators: validateFixedProjectBidDateRange({
            allowZeroDayTimeline,
            startDateKey: 'startDate',
            endDateKey: 'endDate',
            errorText: $localize`End date must be after start date.`,
          }),
        },
      ),
    });

    return detailsForm;
  }

  // Generates a FormArray for creating and editing quotation items.
  // Takes currency and minimum budget to determine validation logic
  // and generate an error message.
  private generateItemForm({
    currency,
    minimumBudget = 0,
  }: {
    currency: Currency;
    minimumBudget: number;
  }): FormArray<FormGroup<QuotationItemFormGroup>> {
    return this.fb.array<FormGroup<QuotationItemFormGroup>>(
      [],
      [
        validateMinimum(minimumBudget, total => {
          const minimumBudgetFormatted = this.currencyPipe.transform(
            minimumBudget,
            currency.code,
          );

          return $localize`The minimum total price is ${minimumBudgetFormatted}.`;
        }),
        validateSubscriptionTotal(minimumBudget, total => {
          const minimumBudgetFormatted = this.currencyPipe.transform(
            minimumBudget,
            currency.code,
          );

          return $localize`Total subscriptions must be larger than ${minimumBudgetFormatted}.`;
        }),
        validateFixedItemsTotal(minimumBudget, total => {
          const minimumBudgetFormatted = this.currencyPipe.transform(
            minimumBudget,
            currency.code,
          );

          return $localize`Total items must be larger than ${minimumBudgetFormatted}.`;
        }),
      ],
    );
  }

  // Initialise Quotation upfront funding form control for quote creation.
  // The quoteTotal should be the upper limit for an upfront funding request.
  initialiseInitialPaymentForm({
    prefillAmount,
    quoteTotal,
  }: {
    prefillAmount?: number;
    quoteTotal?: number;
  }): FormControl<string> {
    const control = new FormControl(
      prefillAmount ? (prefillAmount > 0 ? prefillAmount.toFixed(2) : '') : '',
      { nonNullable: true, validators: [] },
    );

    if (prefillAmount) {
      control.setValidators([
        minValue(0, `Initial payment must not be negative`),
        maxValue(
          quoteTotal ?? 999_999,
          `Deposit cannot be more than the quote total.`,
        ),
        maxDecimalPlaces(2, `Ensure price is no more than 2 decimal places.`),
      ]);
    }
    return control;
  }

  // Initialise Quotation Item Form for editing and adding quotation items.
  // If initial items are provided, the form array is populated with form groups
  // that represent each item and allow existing items to be edited.
  async initialiseQuotationItemForm(
    initialItems: readonly QuotationItemRevision[],
    budget: ProjectBudgetOption,
    currency: Currency,
  ): Promise<FormArray<FormGroup<QuotationItemFormGroup>>> {
    const itemForm = this.generateItemForm({
      currency,
      minimumBudget: budget?.minimum,
    });

    await Promise.all(
      initialItems.map(async item => {
        itemForm.push(
          await this.createItemControl({
            name: item.name,
            price: item.unitPrice * item.quantity,
            order: item.order,
            unitLimit: item.unitLimit,
            unitType: item.unitType,
            itemVersionId: item.id,
            itemId: item.quotationItemId,
            currency,
            itemType: item.quotationItemType,
          }),
        );
      }),
    );

    return itemForm;
  }

  // Use observables to initialise Quotation Item Form Array and populate it with
  // form groups that allow existing items to be edited.
  // When we initialise the quotation item form array, we use this.timeUtils.rxDebounceTime(500)
  // in an attempt to bypass the datastore and avoid retrieving stale
  // cached values on initialisation.
  // TODO: T241008 - Replace debounce: Debouncing time does not guarantee that the
  // retrieved values will not be stale. The initial retrieval should be more robust.
  async initialiseQuotationItemFormRx({
    quotationItems$,
    budget$,
    currency$,
    isUserInAu$ = of(false),
  }: {
    quotationItems$: Observable<readonly QuotationItemRevision[]>;
    budget$: Observable<ProjectBudgetOption>;
    currency$: Observable<Currency>;
    isUserInAu$?: Observable<boolean>;
  }): Promise<FormArray<FormGroup<QuotationItemFormGroup>>> {
    // Initialise empty quotation items form
    const [budget, currency, initialItems, isUserInAu] = await Promise.all([
      firstValueFrom(budget$.pipe(untilDestroyed(this))),
      firstValueFrom(currency$.pipe(untilDestroyed(this))),
      // FIXME: T241008 Improve handling of stale values
      firstValueFrom(
        quotationItems$.pipe(
          this.timeUtils.rxDebounceTime(500),
          untilDestroyed(this),
        ),
      ),
      firstValueFrom(isUserInAu$.pipe(untilDestroyed(this))),
    ]);

    const itemForm = this.generateItemForm({
      currency,
      minimumBudget: budget?.minimum,
    });

    await Promise.all(
      initialItems.map(async item => {
        itemForm.push(
          await this.createItemControl({
            name: item.name,
            price: item.unitPrice * item.quantity,
            order: item.order,
            unitLimit: item.unitLimit,
            unitType: item.unitType,
            itemVersionId: item.id,
            itemId: item.quotationItemId,
            status: item.status,
            isUserInAu,
            currency,
            itemType: item.quotationItemType,
            billingFrequency: item.billingFrequency,
          }),
        );
      }),
    );

    return itemForm;
  }

  async initialisePostApproveQuotationItemForm({
    quotationItems$,
    budget$,
    currency$,
    acceptedQuoteItemIds,
  }: {
    quotationItems$: Observable<readonly QuotationItemRevision[]>;
    budget$: Observable<ProjectBudgetOption>;
    currency$: Observable<Currency>;
    acceptedQuoteItemIds: Set<number>;
  }): Promise<FormArray<FormGroup<QuotationItemFormGroup>>> {
    // Initialise empty quotation items form
    const [budget, currency, initialItems] = await Promise.all([
      firstValueFrom(budget$.pipe(untilDestroyed(this))),
      firstValueFrom(currency$.pipe(untilDestroyed(this))),
      // FIXME: T241008 Improve handling of stale values
      firstValueFrom(
        quotationItems$.pipe(
          this.timeUtils.rxDebounceTime(500),
          untilDestroyed(this),
        ),
      ),
    ]);

    const itemForm = this.generateItemForm({
      currency,
      minimumBudget: budget?.minimum,
    });

    await Promise.all(
      initialItems.map(async item => {
        if (
          acceptedQuoteItemIds.has(item.quotationItemId) ||
          item.status === QuotationItemStatusApi.PENDING
        ) {
          itemForm.push(
            await this.createItemControl({
              name: item.name,
              price: item.unitPrice * item.quantity,
              order: item.order,
              unitLimit: item.unitLimit,
              unitType: item.unitType,
              itemVersionId: item.id,
              itemId: item.quotationItemId,
              status: item.status,
              currency,
              itemType: item.quotationItemType,
            }),
          );
        }
      }),
    );

    // Add an empty form control to encourage the user to create a new item.
    if (initialItems.length === 0) {
      itemForm.push(
        await this.createItemControl({
          name: '',
          price: 0,
          order: this.order.getMiddle('', ''),
          currency,
          itemType: QuotationItemType.FIXED,
        }),
      );
    }

    return itemForm;
  }

  // Populate draft quote with accepted quote information, but using the
  // new item version id. Essentially creating copies in quotation item version
  // as it will eventually do that when items are updated
  async initialiseDraftQuoteWithAcceptedQuote({
    quotationDraft,
    quotationItems$,
    budget$,
    currency$,
  }: {
    quotationDraft: QuotationRevision;
    quotationItems$: Observable<readonly QuotationItemRevision[]>;
    budget$: Observable<ProjectBudgetOption>;
    currency$: Observable<Currency>;
  }): Promise<FormArray<FormGroup<QuotationItemFormGroup>>> {
    const [budget, currency, initialItems] = await Promise.all([
      firstValueFrom(budget$.pipe(untilDestroyed(this))),
      firstValueFrom(currency$.pipe(untilDestroyed(this))),
      firstValueFrom(quotationItems$.pipe(untilDestroyed(this))),
    ]);

    const itemForm = this.generateItemForm({
      currency,
      minimumBudget: budget?.minimum,
    });

    await Promise.all(
      initialItems.map(async item => {
        if (item.status === QuotationItemStatusApi.PENDING) {
          const itemRevisionParams: ItemRevisionParams = {
            previousItemVersionId: item.id,
            quotationVersionId: quotationDraft?.id,
            updateParams: {
              quotationItem: item,
              details: {
                name: item.name,
                unitPrice: item.unitPrice,
                currencyId: quotationDraft.defaultCurrencyId,
                quantity: item.quantity,
                billingFrequency: item.billingFrequency,
              },
            },
          };

          const response = await this.reviseQuotationItem(itemRevisionParams);

          if (response.status === 'success') {
            const itemVersionId: number = response.id;
            itemForm.push(
              await this.createItemControl({
                name: item.name,
                price: item.unitPrice * item.quantity,
                order: item.order,
                unitLimit: item.unitLimit,
                unitType: item.unitType,
                itemVersionId,
                itemId: item.quotationItemId,
                status: item.status,
                currency,
                itemType: item.quotationItemType,
                billingFrequency: item.billingFrequency,
              }),
            );
          } else {
            // T244368: Appropriately handle responses.
          }
        }
      }),
    );
    /* Since the inserting of the itemControls were asynchronous, we have
    to reorder the items. To reorder we can use their order value*/
    itemForm.patchValue(
      itemForm.value.sort((a: any, b: any) => a.order.localeCompare(b.order)),
    );
    // Add an empty form control to encourage the user to create a new item.
    if (initialItems.length === 0) {
      itemForm.push(
        await this.createItemControl({
          name: '',
          price: 0,
          order: this.order.getMiddle('', ''),
          currency,
          itemType: QuotationItemType.FIXED,
        }),
      );
    }

    return itemForm;
  }

  async resetQuotationFormCurrencyValidations(
    itemForm: FormArray<FormGroup<QuotationItemFormGroup>>,
    minimumBudget: number,
    currency: Currency,
    isUserInAu$ = of(false),
  ): Promise<FormArray<FormGroup<QuotationItemFormGroup>>> {
    itemForm.setValidators([
      validateMinimum(minimumBudget, total => {
        const minimumBudgetFormatted = this.currencyPipe.transform(
          minimumBudget,
          currency.code,
        );

        return $localize`The minimum total price is ${minimumBudgetFormatted}.`;
      }),
      validateSubscriptionTotal(minimumBudget, total => {
        const minimumBudgetFormatted = this.currencyPipe.transform(
          minimumBudget,
          currency.code,
        );

        return $localize`Total subscriptions must be larger than ${minimumBudgetFormatted}.`;
      }),
      validateFixedItemsTotal(minimumBudget, total => {
        const minimumBudgetFormatted = this.currencyPipe.transform(
          minimumBudget,
          currency.code,
        );

        return $localize`Total items must be larger than ${minimumBudgetFormatted}.`;
      }),
    ]);

    const isUserInAu = await firstValueFrom(
      isUserInAu$.pipe(untilDestroyed(this)),
    );

    const minHourlyRate = isUserInAu
      ? await this.getMinimumHourlyRateForAusFreelancer(currency.code)
      : await this.getMinimumHourlyRate(currency.id);

    const validations = this.hourlyRateValidators({
      isUserInAu,
      currencyCode: currency.code,
      minHourlyRate,
    });

    itemForm.controls.forEach(group => {
      if (group.value.formType === QuotationItemType.HOURLY) {
        group.setValidators(validations);

        group.updateValueAndValidity();
      }
    });
    itemForm.updateValueAndValidity();
    return itemForm;
  }

  // Create a new control in the items form array
  async createItemControl({
    name,
    price,
    order,
    unitLimit,
    unitType,
    itemVersionId,
    itemId,
    status = QuotationItemStatusApi.PENDING,
    isUserInAu = false,
    currency,
    itemType,
    billingFrequency,
  }: {
    name: string;
    price: number;
    order: string;
    unitLimit?: number;
    unitType?: QuotationItemUnitTypeApi;
    itemVersionId?: number;
    itemId?: number;
    status?: QuotationItemStatusApi;
    isUserInAu?: boolean;
    currency?: Currency;
    itemType: QuotationItemType;
    billingFrequency?: QuotationItemBillingFrequencyApi;
  }): Promise<FormGroup<QuotationItemFormGroup>> {
    let minHourlyRate;
    if (itemType === QuotationItemType.HOURLY) {
      minHourlyRate = isUserInAu
        ? await this.getMinimumHourlyRateForAusFreelancer(
            currency?.code ?? 'USD',
          )
        : await this.getMinimumHourlyRate(currency?.id ?? 1);
    }

    const group = this.fb.group<QuotationItemFormGroup>(
      {
        [ItemFormKeys.NAME]: this.fb.control(name || '', [
          required($localize`Enter a description for the item.`),
          maxLength(
            MAX_ITEM_NAME_LENGTH,
            $localize`Item name should be at maximum ${MAX_ITEM_NAME_LENGTH} characters long.`,
          ),
        ]),
        [ItemFormKeys.PRICE]: this.fb.control(
          price > 0 ? price.toFixed(2) : '',
          itemType === QuotationItemType.FIXED
            ? [
                required($localize`Enter an amount.`),
                minValue(1, $localize`Enter at least 1.`),
                maxValue(999_999_999, $localize`Enter a lesser amount.`),
                maxDecimalPlaces(
                  2,
                  $localize`Ensure price is no more than 2 decimal places.`,
                ),
              ]
            : null,
        ),
        // Hidden field used to keep track of the item version the control represents.
        itemVersionId: this.fb.control(itemVersionId ?? 0),
        itemId: this.fb.control(itemId),
        order: this.fb.control(order),
        status: this.fb.control(status),
        unitLimit: this.fb.control(unitLimit),
        unitType: this.fb.control(unitType),
        formType: this.fb.control(itemType),
      },
      {
        validators:
          itemType === QuotationItemType.HOURLY
            ? this.hourlyRateValidators({
                isUserInAu,
                currencyCode: currency?.code ?? 'USD',
                minHourlyRate,
              })
            : itemType === QuotationItemType.SUBSCRIPTION
              ? [validateSubscriptionAmount()]
              : null,
      },
    );

    if (itemType === QuotationItemType.SUBSCRIPTION) {
      group.addControl(
        'billingFrequency',
        this.fb.control(
          billingFrequency ?? QuotationItemBillingFrequencyApi.MONTHLY,
        ),
      );
    }
    return group;
  }

  hourlyRateValidators({
    isUserInAu,
    currencyCode,
    minHourlyRate,
  }: {
    isUserInAu: boolean;
    currencyCode: string;
    minHourlyRate?: number;
  }): ValidatorFn[] {
    return [
      validateWeeklyLimit({
        minWeeklyLimit: MIN_WEEKLY_LIMIT,
        maxWeeklyLimit: MAX_WEEKLY_LIMIT,
      }),
      validateHourlyRate({
        isUserInAu,
        maxHourlyRate: MAX_HOURLY_RATE,
        minHourlyRate,
        formatAmount: amount => {
          return this.currencyPipe.transform(amount, currencyCode);
        },
      }),
    ];
  }

  getDetailsFormUpdateParams(
    detailsForm: FormGroup<QuotationDetailFormGroup>,
  ): QuotationRevisionUpdateParams {
    const timelineGroup = detailsForm.get(FormKeys.TIMELINE);
    const estimatedStartDate = timelineGroup?.get(FormKeys.START_DATE)?.value;
    const estimatedEndDate = timelineGroup?.get(FormKeys.END_DATE)?.value;
    const description = detailsForm.get(FormKeys.DESCRIPTION)?.value;
    const title = detailsForm.get(FormKeys.TITLE)?.value;
    const expiryDateValue = detailsForm.get(FormKeys.EXPIRY_DATE)?.value;
    const expiryDate = isDefined(expiryDateValue)
      ? getEndOfDayTimestamp(expiryDateValue)
      : undefined;
    return {
      description,
      estimatedStartDate,
      estimatedEndDate,
      title,
      expiryDate,
    };
  }

  /**
   * Datastore helper functions for updating quotation item revisions collection.
   *
   * Each returns a BackendUpdateResponse to handle success/errors.
   */
  updateQuotationItem({
    quotationItem,
    details: {
      name,
      unitPrice,
      quantity,
      unitLimit,
      unitType,
      billingFrequency,
    },
  }: ItemUpdateParams): Promise<
    BackendUpdateResponse<QuotationItemRevisionsCollection>
  > {
    return this.addToQuotationSubmissionBlockList(
      this.datastore
        .collection<QuotationItemRevisionsCollection>('quotationItemRevisions')
        .update(quotationItem.id, {
          name,
          unitPrice,
          quantity,
          unitLimit,
          unitType,
          billingFrequency,
        }),
    );
  }

  getMinimumHourlyRate(currencyId: number): Promise<number> {
    return firstValueFrom(
      this.datastore
        .collection<ProjectBudgetOptionsCollection>(
          'projectBudgetOptions',
          query =>
            query
              .where('projectType', '==', ProjectTypeApi.HOURLY)
              .where('currencyId', '==', currencyId)
              .orderBy('minimum', OrderByDirection.ASC),
        )
        .valueChanges()
        .pipe(
          map(budgets => budgets[0].minimum),
          untilDestroyed(this),
        ),
    );
  }

  async getMinimumHourlyRateForAusFreelancer(
    currencyCode: string,
  ): Promise<number> {
    const audExchangeRateDoc = this.datastore.document<ExchangeRatesCollection>(
      'exchangeRates',
      'AUD',
    );

    const audExchangeRate = await firstValueFrom(
      audExchangeRateDoc.valueChanges().pipe(untilDestroyed(this)),
    );

    const exchangeRateToAUD = audExchangeRate.from[currencyCode];

    return Math.round(AUSTRALIAN_MINIMUM_HOURLY_BID_AMOUNT / exchangeRateToAUD);
  }

  updateQuotationItemStatus({
    itemVersionId,
    status,
  }: {
    itemVersionId: number;
    status: QuotationItemStatusApi;
  }): Promise<BackendUpdateResponse<QuotationItemRevisionsCollection>> {
    return this.addToQuotationSubmissionBlockList(
      this.datastore
        .collection<QuotationItemRevisionsCollection>('quotationItemRevisions')
        .update(itemVersionId, {
          status,
        }),
    );
  }

  deleteQuotationItem({
    itemVersionId,
  }: {
    itemVersionId: number;
  }): Promise<BackendDeleteResponse<QuotationItemRevisionsCollection>> {
    return this.addToQuotationSubmissionBlockList(
      this.datastore
        .collection<QuotationItemRevisionsCollection>('quotationItemRevisions')
        .remove(itemVersionId),
    );
  }

  createQuotationItem(
    quotationId: number,
    quotationVersionId: number,
    {
      currencyId,
      name,
      unitPrice,
      quantity = 1,
      unitLimit,
      unitType,
      billingFrequency,
    }: ItemDetails,
    order: string,
  ): Promise<BackendPushResponse<QuotationItemRevisionsCollection>> {
    return this.addToQuotationSubmissionBlockList(
      this.datastore.createDocument<QuotationItemRevisionsCollection>(
        'quotationItemRevisions',
        {
          // QuotationItemId is a computed field for initial create requests.
          // Setting id to 0 tells the datastore backend to treat this as
          // an initial quotation create req and not a version create req
          quotationItemId: 0,
          quotationId,
          name,
          createDate: Date.now(),
          order,
          currencyId: currencyId ?? 0,
          paidAmount: 0,
          fundedAmount: 0,
          unitPrice,
          description: '',
          quantity,
          quotationVersionId,
          originalQuotationVersionId: quotationVersionId,
          totalPrice: 0,
          unitLimit,
          unitType,
          billingFrequency,
        },
      ),
    );
  }

  reviseQuotationItem({
    previousItemVersionId,
    quotationVersionId,
    updateParams: { quotationItem, details },
  }: ItemRevisionParams): Promise<
    BackendPushResponse<QuotationItemRevisionsCollection>
  > {
    return this.addToQuotationSubmissionBlockList(
      this.datastore.createDocument<QuotationItemRevisionsCollection>(
        'quotationItemRevisions',
        {
          ...quotationItem,
          ...details,
          originalQuotationVersionId: quotationVersionId,
        },
        // Virtual field used in the reducer to replace outdated item version.
        { previousItemVersionId },
      ),
    );
  }

  /**
   * Datastore helper functions for updating quotation revisions collection.
   *
   * Each returns a BackendUpdateResponse to handle success/errors.
   */
  createNewQuotation(
    changes: QuotationCreateParams,
  ): Promise<BackendPushResponse<QuotationRevisionsCollection>> {
    return this.addToQuotationSubmissionBlockList(
      this.datastore.createDocument<QuotationRevisionsCollection>(
        'quotationRevisions',
        {
          // QuotationId is a computed field for initial create requests.
          // Setting id to 0 tells the datastore backend to treat this as
          // an initial quotation create req and not a version create req
          quotationId: 0,
          // Default to false while client-created quotes are not supported
          isRequestedByRecipient: false,
          ...changes,
        },
      ),
    );
  }

  createQuotationDraft({
    originalQuotation,
    changes,
  }: {
    originalQuotation: QuotationRevision;
    changes: QuotationCreateParams;
  }): Promise<BackendUpdateResponse<QuotationRevisionsCollection>> {
    return this.datastore.createDocument<QuotationRevisionsCollection>(
      'quotationRevisions',
      {
        ...originalQuotation,
        ...changes,
        parentVersionId: originalQuotation.id,
      },
    );
  }

  updateQuotationDraft({
    draftQuotation,
    updateParams,
  }: {
    draftQuotation: QuotationRevision;
    updateParams: Partial<QuotationRevisionUpdateParams>;
  }): Promise<BackendUpdateResponse<QuotationRevisionsCollection>> {
    return this.addToQuotationSubmissionBlockList(
      this.datastore
        .collection<QuotationRevisionsCollection>('quotationRevisions')
        .update(draftQuotation.id, {
          ...draftQuotation, // FIXME: T272134
          ...updateParams,
        }),
    );
  }

  async submitQuotation({
    quotationVersionId,
    updateParams,
  }: {
    quotationVersionId: number;
    updateParams?: QuotationRevisionUpdateParams;
  }): Promise<BackendUpdateResponse<QuotationRevisionsCollection>> {
    await this.waitForQuotationToBeSubmittable();
    return this.datastore
      .collection<QuotationRevisionsCollection>('quotationRevisions')
      .update(quotationVersionId, {
        status: QuotationStatusApi.PENDING,
        ...updateParams,
      });
  }

  async updateQuotationStatus({
    quotationVersionId,
    status,
  }: {
    quotationVersionId: number;
    status: Exclude<QuotationStatusApi, QuotationStatusApi.PENDING>;
  }): Promise<BackendUpdateResponse<QuotationRevisionsCollection>> {
    return this.addToQuotationSubmissionBlockList(
      this.datastore
        .collection<QuotationRevisionsCollection>('quotationRevisions')
        .update(quotationVersionId, { status }),
    );
  }

  updateQuotationContext({
    quotationVersionId,
    contextId,
    contextType,
  }: {
    quotationVersionId: number;
    contextId: number;
    contextType: QuotationContextTypeApi;
  }): Promise<BackendUpdateResponse<QuotationRevisionsCollection>> {
    return this.addToQuotationSubmissionBlockList(
      this.datastore
        .collection<QuotationRevisionsCollection>('quotationRevisions')
        .update(quotationVersionId, {
          contextId,
          contextType,
        }),
    );
  }

  updateQuotationUpfrontPayment(
    quotationVersionId: number,
    upfrontFundRequestAmount: number,
  ): Promise<BackendUpdateResponse<QuotationRevisionsCollection>> {
    return this.datastore
      .collection<QuotationRevisionsCollection>('quotationRevisions')
      .update(quotationVersionId, {
        upfrontFundRequestAmount,
      });
  }

  /**
   * Get a draft quote for a given creator/context.
   * Since there will only ever be 1 draft at a given time, we return only 1 quote.
   */
  getDraftQuote({
    creatorId,
    contextId,
    contextType,
  }: {
    creatorId: number;
    contextId: number;
    contextType: QuotationContextTypeApi;
  }): Promise<QuotationRevision | undefined> {
    return firstValueFrom(
      this.datastore
        .collection<QuotationRevisionsCollection>('quotationRevisions', query =>
          query
            .where('creatorId', '==', creatorId)
            .where('contextType', '==', contextType)
            .where('contextId', '==', contextId)
            .where('status', '==', QuotationStatusApi.DRAFT)
            .limit(1),
        )
        .valueChanges()
        .pipe(
          map(quotations =>
            quotations.length > 0 ? quotations[0] : undefined,
          ),
          untilDestroyed(this),
        ),
    );
  }

  getLatestBackendErrorResponse(): Observable<
    BackendPushErrorResponse<QuotationRevisionsCollection>
  > {
    return this.latestBackendErrorResponse$;
  }

  /**
   * Gets the draft quote with provided params, or creates a new draft quote if it doesn't exist
   * @returns The draft quote, or undefined if creation fails (wrapped in a Promise)
   */
  async getOrCreateDraftQuote(
    params: QuotationCreateParams,
  ): Promise<QuotationRevision | undefined> {
    const draftQuotation = await this.getDraftQuote({
      creatorId: params.creatorId,
      contextId: params.contextId,
      contextType: params.contextType,
    });

    if (isDefined(draftQuotation)) {
      return draftQuotation;
    }

    // Draft quote doesn't exist so we create a new one
    const res = await this.createNewQuotation(params);

    if (res.status === 'success') {
      // The API response from the create query contains all the quote fields as a fetch query
      // However, the way the datastore is set up allows only the numerical id in the response
      // If needed, we can try to add uids to the response to avoid another fetch here
      // (for routing purposes), but for now we are re-fetching it to get the new quote
      return this.getDraftQuote({
        creatorId: params.creatorId,
        contextId: params.contextId,
        contextType: params.contextType,
      });
    }

    this.latestBackendErrorResponseSubject$.next(res);

    return undefined;
  }

  /**
   * Gets the pending child quote of the given parent version id.
   * Expects pending child quotes to only be of context type USER or BID
   * BID will happen if the hireme project was already created but payment wasn't made and quote wasn't accepted yet.
   * @returns The pending child quote, or undefined if it doesn't exist
   */
  async getPendingTemplateChildQuote({
    contextId,
    parentTemplateVersionId,
  }: {
    contextId: number;
    parentTemplateVersionId: number;
  }): Promise<QuotationRevision | undefined> {
    /**
     * A child quote is created with a USER context but it should be immediately updated to BID context as they are taken to the cart.
     * A child quote that is directly created through API can have a USER context but that's not the normal flow.
     *
     * One call should be able to handle BID context child quotes, but just to be safe
     * we have a second call here to deal with USER context child quotes.
     */
    const quotation = await firstValueFrom(
      this.datastore
        .collection<QuotationRevisionsCollection>('quotationRevisions', query =>
          query
            .where('contextType', '==', QuotationContextTypeApi.BID)
            .where('parentTemplateVersionId', '==', parentTemplateVersionId)
            .where('status', '==', QuotationStatusApi.PENDING)
            .limit(1),
        )
        .valueChanges()
        .pipe(
          map(quotations =>
            quotations.length > 0 ? quotations[0] : undefined,
          ),
          untilDestroyed(this),
        ),
    );

    if (isDefined(quotation)) {
      return quotation;
    }

    return firstValueFrom(
      this.datastore
        .collection<QuotationRevisionsCollection>('quotationRevisions', query =>
          query
            .where('contextType', '==', QuotationContextTypeApi.USER)
            .where('contextId', '==', contextId)
            .where('parentTemplateVersionId', '==', parentTemplateVersionId)
            .where('status', '==', QuotationStatusApi.PENDING)
            .limit(1),
        )
        .valueChanges()
        .pipe(
          map(quotations =>
            quotations.length > 0 ? quotations[0] : undefined,
          ),
          untilDestroyed(this),
        ),
    );
  }

  /**
   * Gets the pending child quote of the given parent version id, or creates a new child quote if it doesn't exist
   * @returns The pending child quote, or undefined if creation fails (wrapped in a Promise)
   */
  async getOrCreateTemplateChildQuote(
    params: QuotationCreateParams & { parentTemplateVersionId: number }, // override optional
  ): Promise<QuotationRevision | undefined> {
    const pendingTemplateChildQuote = await this.getPendingTemplateChildQuote({
      contextId: params.contextId,
      parentTemplateVersionId: params.parentTemplateVersionId,
    });

    if (isDefined(pendingTemplateChildQuote)) {
      return pendingTemplateChildQuote;
    }

    // Pending child quote doesn't exist so we create a new one
    const res = await this.createNewQuotation(params);

    if (res.status === 'success') {
      // The API response from the create query contains all the quote fields as a fetch query
      // However, the way the datastore is set up allows only the numerical id in the response
      // If needed, we can try to add uids to the response to avoid another fetch here
      // (for routing purposes), but for now we are re-fetching it to get the new quote
      return this.getPendingTemplateChildQuote({
        contextId: params.contextId,
        parentTemplateVersionId: params.parentTemplateVersionId,
      });
    }
    return undefined;
  }

  /**
   * Wrapper function to be used around promises(datastore calls), such that quotation form submissions
   * will wait until the wrapped promise has been fulfilled, to stop any evil concurrency issues.
   *
   * @param promise is Promise that we want to block quotation submission until fulfilled.
   * @returns the same promise, unaltered.
   */
  private addToQuotationSubmissionBlockList<T>(
    promise: Promise<T>,
  ): Promise<T> {
    this.apiCallsInTransit++;
    promise.finally(() => {
      // Remove the call when it fulfills, either by success or failure
      this.apiCallsInTransit--;
      this.checkIfQuoteIsSubmittable();
    });
    return promise;
  }

  /**
   * Does a check to see if the quotation is allowed to be submitted,
   * taking into account existing api calls.
   * Does not return anything, but resolves `quoteSubmittablePromise`
   */
  private checkIfQuoteIsSubmittable(): void {
    if (this.apiCallsInTransit === 0) {
      if (this.quoteSubmitPromiseResolver !== undefined) {
        this.quoteSubmitPromiseResolver(undefined);
      }

      // clear variables
      this.quoteSubmittablePromise = undefined;
      this.quoteSubmitPromiseResolver = undefined;
    }
  }

  /**
   * Returns a promise that will resolve once the quotation form has no ongoing api calls,
   * and thus is ready to be submitted.
   */
  async waitForQuotationToBeSubmittable(): Promise<undefined> {
    if (!isDefined(this.quoteSubmittablePromise)) {
      this.quoteSubmittablePromise = new Promise(resolve => {
        if (this.apiCallsInTransit === 0) {
          resolve(undefined);
          return;
        }
        this.quoteSubmitPromiseResolver = resolve;
      });
    }

    return this.quoteSubmittablePromise;
  }

  /**
   * Create a quotation logo and set it as default, then return the response.
   */
  async createQuotationLogo(
    fileUuid: string,
  ): Promise<BackendPushResponse<QuotationLogosCollection>> {
    return this.datastore.createDocument<QuotationLogosCollection>(
      'quotationLogos',
      {
        fileUuid,
        isDefault: true,
      },
    );
  }

  /**
   * Update a quotation logo to unset it's default state, then return the response.
   */
  removeDefaultLogo(
    logoId: number,
  ): Promise<BackendUpdateResponse<QuotationLogosCollection>> {
    const quotationLogoCollection =
      this.datastore.document<QuotationLogosCollection>(
        'quotationLogos',
        logoId,
      );
    return quotationLogoCollection.update({
      isDefault: false,
    });
  }

  /**
   * Get the service offering price string from the items.
   */
  getServiceOfferingPriceString(
    items: readonly QuotationItemRevision[],
    currency: Currency,
  ): string {
    let finalPriceText: string;
    const hourlyPrice: ServiceOfferingPrice = { amount: 0 };
    const fixedPrice: ServiceOfferingPrice = { amount: 0 };
    const subscriptionPrice: ServiceOfferingPrice = { amount: 0 };

    items.map(item => {
      if (isDefined(item.unitType)) {
        hourlyPrice.amount += item.unitPrice;
      } else if (isDefined(item.billingFrequency)) {
        subscriptionPrice.amount += item.unitPrice;
      } else {
        fixedPrice.amount += item.unitPrice;
      }
    });

    /* Flow for this is as follows:
     * 1. If there is a fixed price, show fixed + subscription price with "... from PRICE USD" format..
     * 2. If there is a subscription and an hourly item, show subscription price with "... from PRICE USD" format.
     * 3. If there is only a subscription item, show subscription price with "... for PRICE / month" format.
     * 4. Default is an hourly item, show hourly price with format "... for PRICE / hr" format. */

    if (fixedPrice.amount || (subscriptionPrice.amount && hourlyPrice.amount)) {
      finalPriceText = `from ${this.currencyPipe.transform(
        fixedPrice.amount + subscriptionPrice.amount,
        currency.code,
      )}`;
    } else if (subscriptionPrice.amount) {
      finalPriceText = `for ${this.currencyPipe.transform(
        subscriptionPrice.amount,
        currency.code,
      )} / month`;
    } else {
      finalPriceText = `for ${this.currencyPipe.transform(
        hourlyPrice.amount,
        currency.code,
      )} / hour`;
    }

    return finalPriceText;
  }
}

/**
 * @returns Unix timestamp in milliseconds representing 23:59:59 in local time of the input date
 */
export function getEndOfDayTimestamp(date: Date): number {
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 0);
  return newDate.getTime();
}
